<template>
  <div class="vd__login-container">
    <div class="vd__login_form_container">
      <div class="vd__login_logo"><icon-videsk/></div>
      <div class="vd__login_title">{{ $t('login.title') }}</div>
      <p v-show="showOtp || enterpriseSSO" @click="backLogin" style="cursor: pointer;">
        <a-icon type="left" style="height: 0.8rem; margin-right: 5px;"/>
        Volver
      </p>
      <a-form
        :form="form"
        @submit="handleSubmit">
        <!-- Email -->
        <a-form-item
          v-show="!showOtp"
          class="vd__input_login"
          :wrapper-col="{ span: 48 }">
          <a-input
            size="large"
            type="text"
            :placeholder="$t('login.email')"
            :disabled="loading"
            v-decorator="[
              'email',
              {rules: [ { required: true,
              message: this.$t('login.errors.email') }]}
            ]"><a-icon slot="suffix" type="user" /></a-input>
        </a-form-item>
        <!-- Password -->
        <a-form-item
          v-show="!hideControls"
          class="vd__input_login"
          :wrapper-col="{ span: 48 }">
          <a-input
            size="large"
            type="password"
            :disabled="loading"
            :placeholder="$t('login.password')"
            v-decorator="['password',
            {rules: [ { required: true,
            message: this.$t('login.errors.password') }]}]">
            <a-icon slot="suffix" type="lock" />
          </a-input>
        </a-form-item>
        <a-form-item
          v-show="showOtp"
          class="vd__input_login"
          :wrapper-col="{ span: 48 }">
          <a-input
            size="large"
            type="text"
            placeholder="2FA"
            v-decorator="['otp']">
            <a-icon slot="suffix" type="lock" />
          </a-input>
        </a-form-item>
        <!-- Remember checkbox -->
        <a-form-item
          v-show="!hideControls"
          :wrapper-col="{ span: 48 }">
          <a-checkbox
            @change="rememberCheckbox"
            v-decorator="['remember', {  valuePropName: 'checked', initialValue: false }]"
          >{{ $t('login.remember') }}</a-checkbox>
        </a-form-item>
        <a-form-item
          style="margin-top: -30px;"
          :wrapper-col="{ span: 48 }">
          <a-checkbox @change="termsCheckbox"
                      v-decorator="['terms',
                      {  valuePropName: 'checked',
                      initialValue: false, rules: [{ validator: (_, value) => value, message: $t('login.errors.terms') }] }]">{{ $t('login.terms') }} <a href="http://videsk.io/legal" target="_blank">{{ $t('login.terms2') }}</a>.</a-checkbox>
        </a-form-item>
        <!-- Login button -->
        <a-form-item
          v-show="!enterpriseSSO"
          :wrapper-col="{ span: 48 }"
        >
          <a-button
            size="large"
            type="primary"
            html-type="submit"
            :loading="loading"
            icon="login"
            block>
            {{ $t('login.login') }}
          </a-button>
        </a-form-item>
        <a-divider v-show="!enterpriseSSO" />
        <a-form-item
          v-show="!showOtp"
          :wrapper-col="{ span: 48 }"
        >
          <a-button
            size="large"
            :loading="loading"
            icon="team"
            @click="enterpriseSSOLogin()"
            block>
            {{ $t('login.loginSSO') }}
          </a-button>
        </a-form-item>
      </a-form>
      <!-- End login form -->
      <!-- Forget password option -->
      <div class="vd__forgot-password-container">
        <div @click="$router.push({ name: 'password-forgot' })">{{ $t('login.forgot') }}</div>
      </div>
    </div>
    <div class="vd__login_promo_container"></div>
  </div>
</template>

<script>
import Sentry from '@/plugins/sentry';
import icon from '@/components/icon-component.vue';
import Pendo from '@/plugins/pendo';

export default {
  components: {
    'icon-videsk': icon,
  },
  props: {
    id: String
  },
  data() {
    return {
      form: this.$form.createForm(this),
      formLayout: 'vertical',
      auth: null,
      loading: false,
      termsCheck: false,
      hideControls: false,
      params: null,
      enterpriseSSO: false,
      showOtp: false,
    };
  },
  computed: {
    isSSO() {
      return this.params && this.params.get('sso');
    },
  },
  mounted() {
    this.params = new URLSearchParams(window.location.search);
    if (this.id) return this.claimSession();
  },
  methods: {
    getFields() {
      return new Promise(resolve => {
        this.form.validateFieldsAndScroll((error, data) => resolve([error, data]));
      });
    },
    backLogin() {
      this.showOtp = false;
      this.enterpriseSSO = false;
      this.hideControls = false;
      this.loading = false;
    },
    enterpriseSSOLogin() {
      this.hideControls = true;
      this.enterpriseSSO = true;
      if (this.form.getFieldValue('email')) this.handleSubmit(new Event('login'));
    },
    async claimSession() {
      this.loading = true;
      const response = await this.$http.get(`/auth/login/${this.id}`).catch(e => e);
      if (response instanceof Error) return this.errorOnLogin(response);
      await this.successLogin(response);
      this.loading = false;
    },
    async handleSubmit(e) {
      e.preventDefault();
      this.loading = true;

      const [errors, values] = await this.getFields();
      if (errors && !this.enterpriseSSO) return this.loading = false;

      const {
        email, password, remember, otp,
      } = values;

      const response = await this.axios.post('/auth/login', {
        strategy: 'local', email, password, otp, agreement: this.termsCheck, remember,
      }, { headers: { 'X-Redirect': '*' } }).catch(e => e);

      if (response instanceof Error) return this.errorOnLogin(response);

      await this.successLogin(response);
      this.loading = false;
    },
    async successLogin(response) {
      const {
        level, accessToken, refreshToken, user, redirect, location, remember
      } = response.data;
      if (redirect) window.location.href = location;
      if (this.isSSO) return this.redirectSSO(accessToken);
      if (level > 4) return this.redirectToConsole();

      await this.$authorization.login(accessToken, refreshToken, remember);
      this.setAuthorizationHeader();

      // eslint-disable-next-line no-underscore-dangle
      await this.getUserMetadata(user._id, user.account);

      Sentry.setUser({
        id: this.$store.getters['global/id'],
        account: this.$store.getters['global/account'],
        username: this.$store.getters['global/email'],
      });

      this.setPendo();

      const redirectParameters = this.getRedirectInTheURL();
      if (redirectParameters) return this.$router.push(redirectParameters);

      await this.$router.push({ name: 'segments' });
      this.$notification.success({ message: `${this.$t('login.correct')} 😁` });
      this.backLogin();
    },
    getRedirectInTheURL() {
      const search = new URLSearchParams(window.location.search);
      const redirect = search.get('redirect');
      if (!redirect) return undefined;
      const [path, redirectParams] = redirect.split('?');
      if (['/', ''].includes(path)) return undefined;
      const query = this.$urls.searchParams(redirectParams);
      return { path, query };
    },
    setAuthorizationHeader() {
      const accessToken = this.$authorization.getTokens('accessToken');
      this.axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    },
    rememberCheckbox(e) {
      this.checkNick = e.target.checked;
      this.$nextTick(() => {
        this.form.validateFields(['email'], { force: true });
      });
    },
    termsCheckbox(e) {
      this.termsCheck = e.target.checked;
    },
    getUserMetadata(userId, account) {
      this.$store.commit('global/account', account);
      this.$store.commit('global/id', userId);
      return this.$store.dispatch('global/getUserData');
    },
    redirectToConsole() {
      this.$notification.error({ message: this.$t('login.errors.level') });
      setTimeout(() => window.location.replace('https://console.videsk.io'), 5000);
    },
    errorOnLogin(error) {
      this.loading = false;
      if (!error) return this.$notification.error({ message: 'Uuups! 😱', description: this.$t('login.errors.incorrect') });
      const { status } = error.response || {};
      if (!status) this.$notification.error({ message: 'Al parecer no estás conectado a Internet.' });
      else if (status === 400) this.$notification.error({ message: this.$t('login.errors.terms') });
      else if (status === 401) this.$notification.error({ message: !this.hideControls ? `${this.$t('login.errors.incorrect')} 😨` : this.$t('login.errors.2fa') });
      else if (status === 402) this.$notification.error({ message: this.$t('login.errors.subscriptions') });
      else if (status === 403) this.$notification.error({ message: this.$t('login.errors.ip') });
      else if (status === 412) {
        this.showOtp = true;
        this.hideControls = true;
      }
      else if (status === 410) this.$notification.error({ message: this.$t('login.errors.attempts') });
      else if (status === 423) this.$notification.error({ message: this.$t('login.errors.session') });
      else this.$notification.error({ message: `${this.$t('login.errors.global')}` });
    },
    setPendo() {
      Pendo(
        {
          firstname: this.$store.getters['global/user'].firstname,
          lastname: this.$store.getters['global/user'].lastname,
          email: this.$store.getters['global/user'].email,
          id: this.$store.getters['global/id'],
        },
        {
          // eslint-disable-next-line no-underscore-dangle
          id: this.$store.getters['global/accountObj']._id,
          name: this.$store.getters['global/accountObj'].name,
        },
      );
    },
    redirectSSO(accessToken) {
      if (this.params.get('sso') === 'feedback') return this.redirectToFeedback(accessToken);
    },
    async redirectToFeedback(accessToken) {
      const ssoToken = await this.$ssoToken('feedback', accessToken);
      const redirectURL = new URLSearchParams();
      const finalRedirectURL = decodeURIComponent(this.params.get('redirect'));
      redirectURL.set('ssoToken', ssoToken);
      window.location.assign(`${finalRedirectURL}?${decodeURIComponent(redirectURL.toString())}`);
    },
    mixpanel() {
      this.$mixpanel.identify(this.$store.getters['global/id']);
      this.$mixpanel.set_group('company', this.$store.getters['global/accountObj'].name);
      this.$mixpanel.people.set({
        $first_name: this.$store.getters['global/firstname'],
        $last_name: this.$store.getters['global/lastname'],
        $email: this.$store.getters['global/email'],
        Business: this.$store.getters['global/accountObj'].name,
      });
    },
  },
};
</script>

<style>
.vd__login-container{
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
}
  .vd__login_form_container{
    width: 35%;
    height: 100%;
    padding: 4%;
    background: white;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .vd__login_promo_container{
    width: 65%;
    height: 100%;
    padding: 0;
    margin: 0;
    background: linear-gradient(225deg, #0ab3e4, #102976);
  }

  .vd__login_logo{
    width: 80px;
    height: 80px;
    margin-bottom: 15px;
  }

  .vd__login_title{
    font-size: 2.3em;
    font-weight: bold;
    margin-bottom: 25px;
    color: #585858;
  }

  .vd__input_login{
    margin-bottom: 10px;
  }

  .vd__forgot-password-container{
    color: #005bea;
    margin: 10px 0;
    cursor: pointer;
    text-align: right;
    opacity: 0.7;
    transition: all .3s ease;
  }

.vd__forgot-password-container:hover{
  opacity: 1;
  transition: all .3s ease;
}
</style>

<style>
  .vd__svg_icon_videsk{
    fill: #005bea;
  }
</style>
